import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { servicesAPI } from '../lib/api';
import { useCart } from '../context/CartContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { formatPrice } from '../lib/utils';
import { Check } from 'lucide-react';

export default function ServiceDetail() {
  const { id } = useParams();
  const [service, setService] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    loadService();
  }, [id]);

  const loadService = async () => {
    try {
      const response = await servicesAPI.getById(id);
      setService(response.data);
      setSelectedTier(response.data.pricing_tiers[0]);
    } catch (error) {
      toast.error('Failed to load service');
    }
  };

  const handleAddToCart = () => {
    addToCart(service, selectedTier);
    toast.success('Added to cart!');
    navigate('/cart');
  };

  if (!service) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8">
        <div>
          <h1 className="text-4xl font-heading font-bold mb-4">{service.name}</h1>
          <p className="text-lg text-muted-foreground">{service.short_description}</p>
        </div>

        {service.image_url && (
          <img src={service.image_url} alt={service.name} className="w-full h-64 object-cover rounded-xl" />
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-line">{service.description}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Features</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <Check className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <div>
            <Card className="sticky top-20">
              <CardHeader>
                <CardTitle>Select Plan</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Accordion type="single" collapsible defaultValue="pricing">
                  <AccordionItem value="pricing">
                    <AccordionTrigger>Pricing Options</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2">
                        {service.pricing_tiers.map((tier, idx) => (
                          <button
                            key={idx}
                            onClick={() => setSelectedTier(tier)}
                            className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                              selectedTier === tier
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-primary/50'
                            }`}
                            data-testid={`tier-${idx}`}
                          >
                            <div className="font-semibold">{tier.name}</div>
                            <div className="text-2xl font-bold text-primary">{formatPrice(tier.price)}</div>
                            {tier.duration_months > 0 && (
                              <div className="text-sm text-muted-foreground">{tier.duration_months} months</div>
                            )}
                          </button>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                {selectedTier && (
                  <div className="pt-4 border-t">
                    <div className="flex justify-between mb-2">
                      <span>Subtotal:</span>
                      <span className="font-semibold">{formatPrice(selectedTier.price)}</span>
                    </div>
                    <div className="flex justify-between mb-2 text-sm text-muted-foreground">
                      <span>VAT (24%):</span>
                      <span>{formatPrice(selectedTier.price * 0.24)}</span>
                    </div>
                    <div className="flex justify-between mb-4 text-lg font-bold">
                      <span>Total:</span>
                      <span className="text-primary">{formatPrice(selectedTier.price * 1.24)}</span>
                    </div>
                    <Button className="w-full" onClick={handleAddToCart} data-testid="add-to-cart-button">
                      Add to Cart
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
